import { useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, Mail, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useSEO";

const Contact = () => {
  useSEO({
    title: "Contact Resume Flow AI - Feedback & Support",
    description: "Get in touch with Resume Flow AI. Share feedback, report issues, or ask questions about our free ATS-optimized resume builder.",
    canonical: "/contact",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke("send-contact", {
        body: formData,
      });

      if (error) throw error;

      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-hero relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="bg-orb bg-orb-primary w-[400px] h-[400px] -top-48 -right-48 opacity-25" />
      <div className="bg-orb bg-orb-accent w-[350px] h-[350px] bottom-0 -left-32 opacity-20" />
      
      <Header />
      
      <main className="container mx-auto px-3 sm:px-4 pt-16 sm:pt-24 pb-12 sm:pb-16 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground mb-3 sm:mb-4 animate-slide-up">
              Get in Touch
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground animate-slide-up-delay-1 px-2">
              Have questions, feedback, or suggestions? We'd love to hear from you.
            </p>
          </div>
          
          {/* Contact Form */}
          <Card className="glass-effect-strong p-4 sm:p-6 md:p-8 animate-slide-up-delay-2">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isSubmitting}
                    className="bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={isSubmitting}
                    className="bg-background/50"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="What's this about?"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  disabled={isSubmitting}
                  className="bg-background/50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Tell us how we can help..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  disabled={isSubmitting}
                  className="min-h-[150px] bg-background/50"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full gradient-button text-primary-foreground" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </Card>
          
          {/* Contact Info */}
          <div className="text-center mt-8 sm:mt-12 space-y-3 px-2">
            <p className="text-muted-foreground flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base">
              <Mail className="w-4 h-4 shrink-0" />
              Or email us directly at{" "}
              <a href="mailto:support@resuflow.co" className="text-primary hover:underline">
                support@resuflow.co
              </a>
            </p>
            <p className="text-muted-foreground text-sm">
              Have any other general inquiries?{" "}
              <a href="mailto:inquiry@resuflow.co" className="text-primary hover:underline">
                inquiry@resuflow.co
              </a>
            </p>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/30 py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2024 Resume Flow AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;